import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { CheckResult, Monitor, MonitorDraft, TeamInvitation, Tenant, TenantMember } from './models';
import { RuntimeConfigService } from './runtime-config.service';
@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient); private readonly auth = inject(AuthService); private readonly config = inject(RuntimeConfigService);
  getTenants() { return this.request<Tenant[]>('GET', '/tenants'); }
  createTenant(name: string) { return this.request<Tenant>('POST', '/tenants', { name }); }
  getMonitors(id: string) { return this.request<Monitor[]>('GET', `/tenants/${id}/monitors`); }
  createMonitor(id: string, draft: MonitorDraft) { return this.request<Monitor>('POST', `/tenants/${id}/monitors`, this.monitorPayload(draft)); }
  updateMonitor(id: string, monitorId: string, draft: MonitorDraft) { return this.request<Monitor>('PUT', `/tenants/${id}/monitors/${monitorId}`, this.monitorPayload(draft)); }
  deleteMonitor(id: string, monitorId: string) { return this.request<void>('DELETE', `/tenants/${id}/monitors/${monitorId}`); }
  async getResults(id: string, monitorId: string) {
    const results = await this.request<Array<Omit<CheckResult, 'checkedAt'> & { checkedAt: string | number }>>('GET', `/tenants/${id}/monitors/${monitorId}/results`);
    return results.map((result) => ({ ...result, checkedAt: this.normalizeCheckedAt(result.checkedAt) }));
  }
  getMembers(id: string) { return this.request<TenantMember[]>('GET', `/tenants/${id}/members`); }
  inviteMember(id: string, email: string, role: string) { return this.request<TeamInvitation>('POST', `/tenants/${id}/invitations`, { email, role }); }
  acceptInvitation(token: string) { return this.request<TenantMember>('POST', '/invitations/accept', { token }); }
  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    if (!this.config.apiUrl) throw new Error('API-URL fehlt in src/environments/environment.ts.'); const token = await this.auth.getAccessToken();
    try { return await firstValueFrom(this.http.request<T>(method, `${this.config.apiUrl}${path}`, { body, headers: { Authorization: `Bearer ${token}` } })); }
    catch (error) { if (error instanceof HttpErrorResponse) throw new Error(error.error?.message ?? `API-Fehler (${error.status})`); throw error; }
  }
  private monitorPayload(draft: MonitorDraft) {
    return {
      name: draft.name,
      url: draft.url,
      groupName: draft.groupName?.trim() || '',
      tags: this.normalizeTags(draft.tagsInput),
      enabled: draft.enabled,
      intervalSeconds: draft.intervalSeconds,
      expectedStatusFrom: draft.expectedStatusFrom,
      expectedStatusTo: draft.expectedStatusTo,
      timeoutSeconds: draft.timeoutSeconds,
      responseTimeWarningMillis: draft.responseTimeWarningMillis,
      responseTimeFailureMillis: draft.responseTimeFailureMillis,
      failureConfirmationCount: draft.failureConfirmationCount,
      recoveryConfirmationCount: draft.recoveryConfirmationCount,
      maintenanceStartsAt: this.toInstant(draft.maintenanceStartsAt),
      maintenanceEndsAt: this.toInstant(draft.maintenanceEndsAt),
      maintenanceRecurrence: draft.maintenanceRecurrence,
      maintenanceTimeZone: draft.maintenanceTimeZone,
    };
  }
  private toInstant(value: string | null) { return value ? new Date(value).toISOString() : null; }
  private normalizeTags(value: string) {
    const tags = new Map<string, string>();
    for (const item of value.split(',')) { const tag = item.trim(); if (tag && !tags.has(tag.toLocaleLowerCase())) tags.set(tag.toLocaleLowerCase(), tag); }
    return [...tags.values()];
  }
  private normalizeCheckedAt(value: string | number) {
    if (typeof value === 'string') return value;
    const milliseconds = Math.abs(value) < 1_000_000_000_000 ? value * 1000 : value;
    return new Date(milliseconds).toISOString();
  }
}
